import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevPlanCollaborator } from '../models';
import { toMap } from '../../../../language/arrays';
import { Map } from '../../../../language/models';


const KEY_ACTION = ActionType.DeleteDevPlanCollaboratorRequest;
const ACTIONS    = [
  ActionType.DeleteDevPlanCollaboratorRequest,
  ActionType.DeleteDevPlanCollaboratorResponse,
  ActionType.DeleteDevPlanCollaboratorError,
];


type Payload = string;


type Meta = {
  projectId: number;
  email: string;
};


/**
 * Dispatch this to delete a collaborator from the dev plan for the specified project and participant
 *
 * @param projectId - the database Id for the project the dev plan for which this person should be deleted
 * @param participantId - the database Id for the participant for whom this person should be deleted
 * @param email - the collaborator's email address
 */
export const deleteDevPlanCollaboratorAction = (
  projectId: number,
  participantId: number,
  email: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DeleteDevPlanCollaborator,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
        email:         email,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        email:     email,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${email}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, email } = action.meta;

  const metaTag = `${projectId}-${email}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  let collaborators = toMap(
    R.path<DevPlanCollaborator[]>(['devPlan', 'collaborators', `${projectId}`], successState) ?? [],
    c => `${c.email}`,
  );

  collaborators = R.dissoc(`${email}`, collaborators) as Map<DevPlanCollaborator>;

  return R.assocPath(
    ['devPlan', 'collaborators', `${projectId}`],
    Object.values(collaborators),
    successState,
  );
};

export const deleteDevPlanCollaboratorReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
